/*doc
 ---
 title: Carousel
 name: carousel
 category: React
 ---

 A carousel component that will render a set of slide objects in a given sequence. This carousel is designed to accommodate
 any slide type. It will be the user responsibility to develop the react component to templatize the slide object and feed
 the necessary data by defining the slides in the CarouselStore. The carousel component will be responsible for
 orchestrating all the interactions with the user and to construct the framework where the slide objects will be rendered.

 Required properties:
 * `carouselId` (string): Unique string identifier of carousel.

 Optional properties:

 * `carouselWrapAround` (bool|false): wrap around carousel scrolling.
 * `carouselInitialSlideIndex` (number|0): slide index where to start when first rendering the carousel.
 * `carouselReverseScroll` (bool|false): inverting the scrolling direction of the arrows.
 * `carouselSlidesToShow` (number|1): number of slides to display at any given time.
 * `carouselSlidesToScroll` (number|1): number of slides to scroll when clicking the arrows.
 * `carouselSlidingSpeed` (number|500): sliding speed in milliseconds
 * `carouselStyle` (object|{}): custom styling object to be used to render the outer carousel component.
 * `carouselTitle` (object|null): title to be displayed at the top of the component - object having type, class and text properties to be rendered with React.createElement
 * `arrowSmTopOffset` (number|0): offset to specify vertical position of small arrows.
 * `includeSequenceIndicators` (bool|false): display sequence indicator circles.

 */

import assign from "object-assign";
import React from "react";
import ReactDOM from "react-dom";
import Reflux from "reflux";
import ReactTransitionEvents from "react/lib/ReactTransitionEvents";
import { throttle } from "lodash";

import GrComponentFactory from "../gr_component_factory";

import NextArrow from "./next_arrow";
import PreviousArrow from "./previous_arrow";
import SequenceIndicators from "../sequence_indicators";
import SlideTrack from "./slide_track";
import TooltipTrack from "./tooltip_track";

import carouselDefaultProps from "./defaults/carousel_default_props";
import CarouselEventHandlersMixin from "../../react_mixins/carousel/carousel_event_handlers";
import { computeTrackStyle, computeTrackAnimationStyle } from "../../modules/carousel/carousel_helper";

import CarouselStore from "../../react_stores/carousel_store";
import CarouselActions from "../../react_actions/carousel_actions";

const SET_WIDTHS_THROTTLE_WAIT = 10;

export default GrComponentFactory.createClass({

  displayName: "Carousel",
  mixins: [CarouselEventHandlersMixin, Reflux.connectFilter(CarouselStore,
    "carouselStore",
    function(carouselState) {
      return carouselState.carousels[this.props.carouselId];
    }
  )],

  propTypes: {
    carouselId: React.PropTypes.string.isRequired,
    carouselTitle: React.PropTypes.object,
    carouselWrapAround: React.PropTypes.bool,
    carouselInitialSlideIndex: React.PropTypes.number,
    carouselReverseScroll: React.PropTypes.bool,
    carouselSlidesToShow: React.PropTypes.number,
    carouselSlidesToScroll: React.PropTypes.number,
    carouselSlidingSpeed: React.PropTypes.number,
    carouselStyle: React.PropTypes.object,
    arrowSmTopOffset: React.PropTypes.number,
    includeSequenceIndicators: React.PropTypes.bool,
  },

  getInitialState() {
    return {};
  },

  // This awkward merging is because this was built when Reflux supported setting
  // store state into the top-level state object which is obviously a bad idea
  // because then you can't easily trace where the state is coming from.
  // ...like all the state in this component
  // And the test coverage wasn't sufficient to see what breaks
  getState() {
    return Object.assign({}, this.state, this.state.carouselStore);
  },

  getDefaultProps () {
    return carouselDefaultProps;
  },

  componentDidMount () {
    CarouselActions.setSlides(this.props.carouselId);
    this.setWidths();
    window.addEventListener("resize", throttle(this.setWidths, SET_WIDTHS_THROTTLE_WAIT));
  },

  componentWillUnmount () {
    window.removeEventListener("resize", throttle(this.setWidths, SET_WIDTHS_THROTTLE_WAIT));
  },

  setWidths() {
    const listWidth = this.getWidth(ReactDOM.findDOMNode(this.refs.list));
    const slideContainerWidth = Math.round(listWidth/this.props.carouselSlidesToShow);
    const trackVisibleWidth = slideContainerWidth * this.props.carouselSlidesToShow;
    const widthStateSpecs = {
      listWidth,
      slideContainerWidth,
      trackVisibleWidth,
      currentSlide: this.getState().currentSlide || this.props.carouselInitialSlideIndex,
      carouselSlidesToShow: this.props.carouselSlidesToShow,
      carouselSlidesToScroll: this.props.carouselSlidesToScroll,
      carouselWrapAround: this.props.carouselWrapAround,
    };
    widthStateSpecs.trackStyle = computeTrackStyle(widthStateSpecs);
    CarouselActions.setWidths(this.props.carouselId, widthStateSpecs);
  },

  getWidth (elem) {
    return elem.getBoundingClientRect().width || elem.offsetWidth;
  },

  slideHandler (targetSlide) {
    let adjustedTargetSlide;
    let removalCallback, addCallback;

    if (targetSlide < 0) {
      if(this.props.carouselWrapAround) {
        adjustedTargetSlide = this.getState().slideCount + targetSlide;
      } else {
        adjustedTargetSlide = 0;
      }
    } else if (targetSlide >= this.getState().slideCount) {
      if(this.props.carouselWrapAround) {
        adjustedTargetSlide = targetSlide - this.getState().slideCount;
      } else {
        adjustedTargetSlide = this.getState().slideCount - this.props.carouselSlidesToShow;
      }
    } else {
      adjustedTargetSlide = targetSlide;
    }

    const nextStateChanges = {
      currentSlide: adjustedTargetSlide,
      trackStyle: computeTrackStyle(assign({ targetSlide: adjustedTargetSlide }, this.props, this.getState())),
      swipeLeft: null,
    };
    const fullNextState = {
      carouselStore: Object.assign({}, this.getState(), nextStateChanges),
    };

    removalCallback = () => {
      this.setState(fullNextState);
      ReactTransitionEvents.removeEndEventListener(ReactDOM.findDOMNode(this.refs.track), removalCallback);
      CarouselActions.slidingCompletion(this.props.carouselId, nextStateChanges.currentSlide);
    };

    addCallback = () => {
      ReactTransitionEvents.addEndEventListener(ReactDOM.findDOMNode(this.refs.track), removalCallback);
    };

    const animationTrackStyle = {
      trackStyle: computeTrackAnimationStyle(assign({ targetSlide: this.props.carouselWrapAround ? targetSlide : adjustedTargetSlide, animating: true }, this.props, this.getState())),
    };
    const fullAnimationTrackStyle = {
      carouselStore: Object.assign({}, this.getState(), animationTrackStyle),
    };

    this.setState(fullAnimationTrackStyle, addCallback);

  },

  renderTitle () {
    if (this.props.carouselTitle) {
      const title = React.createElement(this.props.carouselTitle.type || "div", { className: this.props.carouselTitle.class }, this.props.carouselTitle.text);
      return title;
    }
  },

  renderTooltipTrack () {
    if (this.getState().tooltips) {
      const tooltipProps = {
        trackCurrentSlide: this.getState().currentSlide,
        trackWrapAround: this.props.carouselWrapAround,
        trackSlidesCount: this.getState().slideCount,
        trackSlidesToScroll: this.props.carouselSlidesToScroll,
        trackSlidesToShow: this.props.carouselSlidesToShow,
        trackSlideContainerWidth: this.getState().slideContainerWidth,
        trackStyle: {
          width: this.getState().listWidth,
        },
        trackSlideWidth: this.getState().slideContainerWidth,
        trackTooltips: this.getState().tooltips,
        displayTooltipAtIndex: this.getState().displayTooltipAtIndex,
      };

      return (
        <TooltipTrack ref="tooltips" {...tooltipProps}/>
      );
    }
  },

  renderSequenceIndicators() {
    if (this.props.includeSequenceIndicators) {
      return (
        <div className="gr-carousel__slideSequenceIndicatorsContainer">
          <SequenceIndicators count={this.getState().slideCount || 0}
                              selectedIndex={this.getState().currentSlide || 0} />
        </div>
      );
    }
  },

  render () {
    const trackProps = {
      carouselId: this.props.carouselId,
      trackCurrentSlide: this.getState().currentSlide,
      trackWrapAround: this.props.carouselWrapAround,
      trackSlidesCount: this.getState().slideCount,
      trackSlidesToScroll: this.props.carouselSlidesToScroll,
      trackSlidesToShow: this.props.carouselSlidesToShow,
      trackSlideContainerWidth: this.getState().slideContainerWidth,
      trackStyle: this.getState().trackStyle,
      trackSlides: this.getState().slides,
    };

    const arrowProps = {
      carouselId: this.props.carouselId,
      carouselWrapAround: this.props.carouselWrapAround,
      currentSlide: this.getState().currentSlide,
      slideCount: this.getState().slideCount,
      carouselSlidesToShow: this.props.carouselSlidesToShow,
      carouselSlidesToScroll: this.props.carouselSlidesToScroll,
      clickHandler: this.changeSlide,
      carouselReverseScroll: this.props.carouselReverseScroll,
      trackRef: this.refs.track,
      smTopOffset: this.props.arrowSmTopOffset,
    };

    return (
      <div className='gr-carousel' style={this.props.carouselStyle}>
        {this.renderTitle()}
        <div className="gr-carousel__slider">
          <div
            ref="list"
            className="gr-carousel__list"
            onMouseDown={this.swipeStart}
            onMouseUp={this.swipeEnd}
            onTouchStart={this.swipeStart}
            onTouchEnd={this.swipeEnd}>
            <SlideTrack ref="track" {...trackProps}/>
          </div>
          {this.renderTooltipTrack()}
          <PreviousArrow {...arrowProps}/>
          <NextArrow {...arrowProps}/>
          {this.renderSequenceIndicators()}
        </div>
      </div>
    );
  },
});
